import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
  RefreshCw,
  Zap,
} from 'lucide-react';

import {
  fetchModelFallbackStats,
  type ModelFallbackChannelStats,
  type ModelFallbackModelStats,
  type ModelFallbackQualityStats,
  type ModelFallbackStatsRange,
  type ModelFallbackStatsResponse,
} from './modelFallbackStatsApi';

const RANGE_OPTIONS: Array<{ value: ModelFallbackStatsRange; label: string }> = [
  { value: '24h', label: '最近 24 小时' },
  { value: '7d', label: '最近 7 天' },
  { value: '30d', label: '最近 30 天' },
];

const MODE_OPTIONS = [
  { value: '', label: '全部模式' },
  { value: 'text2img', label: '文生图' },
  { value: 'img2img', label: '图生图' },
  { value: 'inpaint', label: '局部重绘' },
];

const QUALITY_OPTIONS = [
  { value: '', label: '全部画质' },
  { value: 'low', label: '低清（1K）' },
  { value: 'medium', label: '高清（2K）' },
  { value: 'high', label: '超清（4K）' },
];

const QUALITY_LABELS: Record<string, string> = {
  low: '低清（1K）',
  medium: '高清（2K）',
  high: '超清（4K）',
  unknown: '未知',
};

function formatRate(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}

function formatMs(value: number | null): string {
  if (value === null) return '—';
  if (value < 1000) return `${value}ms`;
  if (value < 60000) return `${(value / 1000).toFixed(1)}s`;
  return `${(value / 60000).toFixed(1)}min`;
}

function rateColor(value: number | null): string {
  if (value === null) return 'bg-gray-300';
  if (value >= 95) return 'bg-green-500';
  if (value >= 80) return 'bg-yellow-500';
  if (value >= 50) return 'bg-orange-500';
  return 'bg-red-500';
}

function rateTextColor(value: number | null, tone: 'success' | 'warning' = 'success'): string {
  if (value === null) return 'text-light-muted';
  if (tone === 'warning') {
    if (value >= 50) return 'text-red-600';
    if (value >= 20) return 'text-yellow-600';
    return 'text-green-600';
  }
  if (value >= 95) return 'text-green-600';
  if (value >= 80) return 'text-yellow-600';
  if (value >= 50) return 'text-orange-600';
  return 'text-red-600';
}

function RateBar({ value, tone = 'success' }: { value: number | null; tone?: 'success' | 'warning' }) {
  const barColor = tone === 'warning'
    ? (value === null ? 'bg-gray-300' : value >= 50 ? 'bg-red-500' : value >= 20 ? 'bg-yellow-500' : 'bg-green-500')
    : rateColor(value);
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-2 rounded-full transition-all ${barColor}`}
          style={{ width: `${value === null ? 0 : Math.min(value, 100)}%` }}
        />
      </div>
      <span className={`text-xs font-medium ${rateTextColor(value, tone)}`}>{formatRate(value)}</span>
    </div>
  );
}

function StatCard({
  title,
  value,
  suffix,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  suffix?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="rounded-2xl border border-light-border bg-white p-5 shadow-lg">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-light-muted">{title}</p>
          <p className="mt-2 text-2xl font-bold text-light-text sm:text-3xl">
            {typeof value === 'number' ? value.toLocaleString() : value}
            {suffix ? <span className="ml-1 text-base font-medium text-light-muted">{suffix}</span> : null}
          </p>
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function MetricCell({ value, className = '' }: { value: string | number; className?: string }) {
  return <td className={`px-3 py-3 text-right text-sm text-light-text ${className}`}>{value}</td>;
}

function QualityTable({ qualities }: { qualities: ModelFallbackQualityStats[] }) {
  if (qualities.length === 0) {
    return <p className="py-4 text-sm text-light-muted">暂无画质数据</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr className="border-b border-light-border">
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">画质</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">总单</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">首轮成功</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">降级成功</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">链失败</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">内容/参数</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">未归因</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">链成功率</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">恢复率</th>
          </tr>
        </thead>
        <tbody>
          {qualities.map((quality) => (
            <tr key={quality.quality} className="border-b border-light-border last:border-0">
              <td className="px-3 py-2 text-sm text-light-text">
                {QUALITY_LABELS[quality.quality] || quality.quality}
              </td>
              <MetricCell value={quality.totalJobs} />
              <MetricCell value={quality.firstPass} />
              <MetricCell value={quality.recovered} />
              <MetricCell value={quality.chainExhausted} className={quality.chainExhausted > 0 ? 'text-red-600' : ''} />
              <MetricCell value={quality.userBlocked} />
              <MetricCell value={quality.unattributedFailed} />
              <td className="px-3 py-2">
                <RateBar value={quality.chainSuccessRate} />
              </td>
              <td className="px-3 py-2">
                <RateBar value={quality.recoveryRate} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelTable({ channels }: { channels: ModelFallbackChannelStats[] }) {
  if (channels.length === 0) {
    return (
      <p className="py-4 text-sm text-light-muted">
        暂无渠道级尝试记录，可能全部为首轮成功，或尚未进入服务商调用。
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px]">
        <thead>
          <tr className="border-b border-light-border">
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">渠道</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">尝试</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">失败</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">成功</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">尝试成功率</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">平均失败耗时</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-light-muted">平均降级成功耗时</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-light-muted">Top 错误</th>
          </tr>
        </thead>
        <tbody>
          {channels.map((channel) => (
            <tr key={channel.channelId} className="border-b border-light-border last:border-0">
              <td className="px-3 py-2">
                <div className="text-sm text-light-text">{channel.channelName}</div>
                <div className="text-xs text-light-muted">{channel.provider || channel.channelId}</div>
              </td>
              <MetricCell value={channel.attempts} />
              <MetricCell value={channel.failures} className={channel.failures > 0 ? 'text-red-600' : ''} />
              <MetricCell value={channel.successes} />
              <td className="px-3 py-2">
                <RateBar value={channel.attemptSuccessRate} />
              </td>
              <td className="px-3 py-2 text-right text-sm text-light-text">
                {formatMs(channel.avgFailureDurationMs)}
              </td>
              <td className="px-3 py-2 text-right text-sm text-light-text">
                {formatMs(channel.avgRecoveryDurationMs)}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {channel.topErrors.length === 0 ? (
                    <span className="text-xs text-light-muted">—</span>
                  ) : (
                    channel.topErrors.map((error) => (
                      <span
                        key={error.code}
                        className="rounded bg-light-hover px-1.5 py-0.5 text-xs text-light-muted"
                        title={error.code}
                      >
                        {error.code} × {error.count}
                      </span>
                    ))
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModelDetail({ model }: { model: ModelFallbackModelStats }) {
  return (
    <div className="space-y-5 border-t border-light-border bg-light-hover/30 px-4 py-5">
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-4 w-4 text-light-muted" />
          <h4 className="text-sm font-semibold text-light-text">按画质</h4>
        </div>
        <QualityTable qualities={model.qualities} />
      </div>
      <div>
        <div className="mb-3 flex items-center gap-2">
          <Zap className="h-4 w-4 text-light-muted" />
          <h4 className="text-sm font-semibold text-light-text">渠道尝试明细</h4>
        </div>
        <ChannelTable channels={model.channels} />
      </div>
    </div>
  );
}

export default function ModelFallbackStatsTab() {
  const [range, setRange] = useState<ModelFallbackStatsRange>('7d');
  const [model, setModel] = useState('');
  const [mode, setMode] = useState('');
  const [quality, setQuality] = useState('');
  const [data, setData] = useState<ModelFallbackStatsResponse | null>(null);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedModel, setExpandedModel] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await fetchModelFallbackStats({
        range,
        model: model || undefined,
        mode: mode || undefined,
        quality: quality || undefined,
      });
      setData(result);
      if (!model && result.models.length > 0) {
        setModelOptions(result.models.map((item) => item.model));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载模型降级统计失败');
    } finally {
      setLoading(false);
    }
  }, [range, model, mode, quality]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.summary;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-light-border bg-white p-4 shadow-lg">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-light-muted" />
            <div>
              <h3 className="font-semibold text-light-text">模型降级链统计</h3>
              <p className="text-xs text-light-muted">按模型 / 画质查看降级触发、恢复与渠道级尝试成功率</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex rounded-xl border border-light-border p-1">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setRange(option.value)}
                  className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${
                    range === option.value
                      ? 'bg-light-primary text-white'
                      : 'text-light-text hover:bg-light-hover'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <select
              value={model}
              onChange={(event) => setModel(event.target.value)}
              className="rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            >
              <option value="">全部模型</option>
              {modelOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value)}
              className="rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            >
              {MODE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={quality}
              onChange={(event) => setQuality(event.target.value)}
              className="rounded-xl border border-light-border px-3 py-2 text-sm text-light-text focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            >
              {QUALITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center justify-center gap-1 rounded-xl border border-light-border px-3 py-2 text-sm text-light-text transition-colors hover:bg-light-hover disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-light-primary" />
        </div>
      ) : error ? (
        <div className="py-20 text-center">
          <p className="mb-4 text-red-500">{error}</p>
          <button
            onClick={loadData}
            className="rounded-xl bg-light-primary px-4 py-2 text-white transition-colors hover:bg-light-primary/90"
          >
            重试
          </button>
        </div>
      ) : data && summary ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard
              title="总任务数"
              value={summary.totalJobs}
              icon={BarChart3}
              color="bg-blue-500"
            />
            <StatCard
              title="链成功率"
              value={formatRate(summary.chainSuccessRate)}
              icon={Zap}
              color="bg-green-500"
            />
            <StatCard
              title="降级触发率"
              value={formatRate(summary.fallbackTriggerRate)}
              icon={RefreshCw}
              color="bg-yellow-500"
            />
            <StatCard
              title="降级恢复率"
              value={formatRate(summary.recoveryRate)}
              icon={Clock}
              color="bg-purple-500"
            />
            <StatCard
              title="链失败数"
              value={summary.chainExhausted}
              icon={AlertTriangle}
              color="bg-red-500"
            />
          </div>

          <div className="rounded-2xl border border-light-border bg-white p-5 shadow-lg">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-semibold text-light-text">按模型统计</h3>
                <p className="text-xs text-light-muted">点击模型行展开画质明细与渠道尝试明细</p>
              </div>
              {loading ? (
                <span className="flex items-center gap-1 text-xs text-light-muted">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  刷新中
                </span>
              ) : null}
            </div>

            <div className="hidden lg:block">
              <table className="w-full min-w-[1080px]">
                <thead>
                  <tr className="border-b border-light-border">
                    <th className="px-3 py-3 text-left text-sm font-medium text-light-muted">模型</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">总单</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">首轮成功</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">降级成功</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">链失败</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">内容/参数</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">未归因</th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-light-muted">链成功率</th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-light-muted">降级触发率</th>
                    <th className="px-3 py-3 text-left text-sm font-medium text-light-muted">恢复率</th>
                    <th className="px-3 py-3 text-right text-sm font-medium text-light-muted">平均尝试</th>
                    <th className="w-10 px-3 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.models.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-3 py-10 text-center text-sm text-light-muted">
                        当前筛选条件下暂无终态任务
                      </td>
                    </tr>
                  ) : (
                    data.models.map((item) => {
                      const expanded = expandedModel === item.model;
                      return (
                        <Fragment key={item.model}>
                          <tr
                            onClick={() => setExpandedModel(expanded ? null : item.model)}
                            className="cursor-pointer border-b border-light-border transition-colors last:border-0 hover:bg-light-hover/60"
                          >
                            <td className="px-3 py-3 text-sm font-medium text-light-text">{item.model}</td>
                            <MetricCell value={item.totalJobs} />
                            <MetricCell value={item.firstPass} />
                            <MetricCell value={item.recovered} />
                            <MetricCell
                              value={item.chainExhausted}
                              className={item.chainExhausted > 0 ? 'text-red-600' : ''}
                            />
                            <MetricCell value={item.userBlocked} />
                            <MetricCell value={item.unattributedFailed} />
                            <td className="px-3 py-3">
                              <RateBar value={item.chainSuccessRate} />
                            </td>
                            <td className="px-3 py-3">
                              <RateBar value={item.fallbackTriggerRate} tone="warning" />
                            </td>
                            <td className="px-3 py-3">
                              <RateBar value={item.recoveryRate} />
                            </td>
                            <MetricCell value={item.avgAttempts ?? '—'} />
                            <td className="px-3 py-3 text-light-muted">
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </td>
                          </tr>
                          {expanded ? (
                            <tr key={`${item.model}-detail`}>
                              <td colSpan={12} className="p-0">
                                <ModelDetail model={item} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 lg:hidden">
              {data.models.length === 0 ? (
                <p className="py-8 text-center text-sm text-light-muted">当前筛选条件下暂无终态任务</p>
              ) : (
                data.models.map((item) => {
                  const expanded = expandedModel === item.model;
                  return (
                    <div key={item.model} className="overflow-hidden rounded-xl border border-light-border">
                      <button
                        onClick={() => setExpandedModel(expanded ? null : item.model)}
                        className="flex w-full items-center justify-between gap-3 bg-light-hover/40 px-4 py-3 text-left"
                      >
                        <div>
                          <div className="text-sm font-medium text-light-text">{item.model}</div>
                          <div className="mt-1 text-xs text-light-muted">
                            总单 {item.totalJobs} · 降级成功 {item.recovered} · 链失败 {item.chainExhausted}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${rateTextColor(item.chainSuccessRate)}`}>
                            {formatRate(item.chainSuccessRate)}
                          </span>
                          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </button>
                      {expanded ? <ModelDetail model={item} /> : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-light-border bg-white p-4 text-xs leading-6 text-light-muted shadow-lg">
            <p>口径说明：</p>
            <p>· 链成功率 = 完成单 / 总单；降级触发率 =（降级成功 + 链失败）/ 总单；恢复率 = 降级成功 /（降级成功 + 链失败）。</p>
            <p>· 内容/参数拦截来自错误规则的 moderation / param 分类，单列展示，不算渠道责任；未归因失败是无 fallbackTrace 的失败（超时、持久化失败、退款、worker 重启等）。</p>
            <p>· 渠道成功率按实际尝试次数统计；同一渠道重试会累计多次。样本少于 3 次不显示成功率。首轮成功没有耗时记录，平均降级成功耗时只统计降级成功那一次。</p>
            <p>· 画质按用户请求画质统计；强制画质映射（forceQualityMapping）影响实际上游画质，但不改变这里的统计口径。</p>
          </div>
        </>
      ) : null}
    </div>
  );
}
